import { action, computed, observable } from 'mobx'
import type {
  CreateCustomerFn,
  GetCurrentUserWorkspaceMembershipsFn,
} from '@/features/iam'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'

export class AddCustomerToWorkspaceDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace | null = null

  /**
   * Validation rules for the add customer to workspace form.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
  })

  constructor(
    private readonly _user: () => SearchableUser,
    private readonly _onAdded: () => Promise<void>,
    private readonly _createCustomer: CreateCustomerFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create customers in.
   * Filters by CUSTOMER_CREATE permission and external customer settings.
   */
  @computed
  get currentUserWorkspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => {
        const canCreateCustomers = mb.member.permissions.customerCreate
        const config = mb.workspace.config.externalCustomer
        const isEnabled = config.enabled && config.allowRequestingMagicLinks
        return canCreateCustomers && isEnabled
      })
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces the currently selected user (i.e. the one being viewed)
   * is already a member of.
   */
  @computed
  get selectedUserWorkspaces() {
    return this.user.memberships.map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that the current user may create customers in
   * AND that the currently selected user is NOT already a member of.
   */
  @computed
  get availableWorkspaces() {
    return this.currentUserWorkspaces.filter(
      (cuws) =>
        !this.selectedUserWorkspaces.some((suws) => suws.id === cuws.id),
    )
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace?: Workspace | null) {
    this.workspace = workspace ?? null
  }

  /**
   * Reset the view model to its default state.
   */
  @action.bound
  reset() {
    this.setWorkspace(null)
    this.validation.reset()
  }

  /**
   * Add the currently selected customer to the selected workspace via magic link.
   */
  submit = task.resolved(async () => {
    const workspace = this.workspace
    if (!this.validation.reset().validate().isValid || !workspace) {
      return
    }

    await this._createCustomer({
      workspaceId: workspace.id,
      email: this.user.email,
      givenName: this.user.givenName,
      familyName: this.user.familyName,
      phoneNumber: this.user.phone || undefined,
    }).catch(this._createAlertErrorHandler('Sending the invitation failed'))

    const displayName = this.user.displayName
    const workspaceName = this.workspace?.name

    this._onAdded().catch(() => undefined)

    this._newAlert()
      .withState('success')
      .saying(
        'Success!',
        `Magic link invitation sent to ${displayName} for ${workspaceName}.`,
      )
      .autoDismiss(2500)
      .show()

    this.modal.close()
  })
}
